import { format, formatDistanceToNowStrict, isToday, isYesterday, parseISO } from 'date-fns';

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = parseISO(iso);
  if (isToday(d)) return `Today ${format(d, 'h:mm a')}`;
  if (isYesterday(d)) return `Yesterday ${format(d, 'h:mm a')}`;
  return format(d, 'MMM d, yyyy h:mm a');
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return format(parseISO(iso), 'MMM d, yyyy');
}

export function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  return `${formatDistanceToNowStrict(parseISO(iso), { addSuffix: true })}`;
}

export function fmtNumber(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString();
}

export function fmtPercent(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${n.toFixed(n % 1 === 0 ? 0 : 1)}%`;
}

export function truncate(s: string, max = 80): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** "Tue, Sep 22, 6:30 AM" in a specific IANA zone (falls back to the browser zone when invalid). */
export function fmtDateTimeInZone(iso: string | null | undefined, timeZone: string | null | undefined): string {
  if (!iso) return '—';
  const d = parseISO(iso);
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: timeZone ?? undefined, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(d);
  } catch {
    return format(d, 'EEE, MMM d h:mm a');
  }
}

/** "3 PM" in a zone. */
export function fmtHourInZone(iso: string, timeZone: string | null | undefined): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: timeZone ?? undefined, hour: 'numeric' }).format(parseISO(iso));
  } catch {
    return format(parseISO(iso), 'h a');
  }
}

/** Short zone label like "EDT" / "CST". */
export function tzAbbreviation(timeZone: string | null | undefined, at = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: timeZone ?? undefined, timeZoneName: 'short' }).formatToParts(at).find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/** Local datetime-input value (YYYY-MM-DDTHH:MM) for a Date. */
export function toDateTimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
